import { FC, useDeferredValue, useEffect, useState } from "react"
import {
  makeStyles,
  Typography,
  ButtonBase,
  Button,
  TextField,
  CircularProgress,
} from "@material-ui/core"
import clsx from "clsx"
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp"
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown"

import SearchIcon from "../atoms/SearchIcon"
import TechniqueFilter from "../molecules/TechniqueFilter"
import DisabledActionTooltip from "../molecules/DisabledActionTooltip"
import { ScheduleDialog } from "./ScheduleDialog"
import ScheduleOverview from "./ScheduleOverview"

import {
  ClientFragment,
  NudgeFragment,
  useGetActiveTechniquesAndInterventionsQuery,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useUser } from "../../hooks/useUser"
import useOrganizationFeatureFlags from "../../hooks/useOrganizationFeatureFlags"
import useTechniqueSearch from "../../hooks/useTechniqueSearch"
import { NudgeDomain, nudgeOrder } from "../../utils/constants"
import { colors } from "../../utils/theme"
import { ScheduleItem, UnscheduledItem } from "../../utils/types"

const SCHEDULING_DISABLED_TOOLTIP =
  "This action is disabled because the client is disconnected."

/* A card component for displaying each technique */
const TechniqueCard: FC<{
  techniqueName: string
  color: { border: string; background: string }
  disabled?: boolean
  onClick?: () => void
}> = ({ techniqueName, color: { border, background }, disabled, onClick }) => {
  const classes = useCardStyles({ border, background })
  return (
    <DisabledActionTooltip
      disabled={Boolean(disabled)}
      text={SCHEDULING_DISABLED_TOOLTIP}
    >
      <span>
        <button
          className={clsx(classes.card, disabled && classes.cardDisabled)}
          onClick={onClick}
          disabled={disabled}
        >
          <Typography variant="body1" className={clsx(classes.cardText)}>
            {techniqueName.replace(" (Specify)", "")}
          </Typography>
        </button>
      </span>
    </DisabledActionTooltip>
  )
}

/* An expandable section of techniques grouped by domain */
const TechniqueSection: FC<{
  title: string
  isExpanded: boolean
  nudges: NudgeFragment[]
  color: { border: string; background: string }
  canSchedule?: boolean
  onClick: (nudge: NudgeFragment) => void
  onToggleExpanded: () => void
}> = ({
  title,
  nudges,
  color,
  canSchedule,
  onClick,
  isExpanded,
  onToggleExpanded,
}) => {
  const classes = useStyles()
  const contentId = `schedule-section-${String(title)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}`
  const titleId = `${contentId}-title`

  // Don't show sections without nudges
  if (nudges.length === 0) return null

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <ButtonBase
          className={classes.sectionHeaderButton}
          onClick={onToggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          aria-labelledby={titleId}
        >
          {isExpanded ? (
            <KeyboardArrowUp className={classes.sectionHeaderIcon} />
          ) : (
            <KeyboardArrowDown className={classes.sectionHeaderIcon} />
          )}
          <Typography
            id={titleId}
            variant="h6"
            component="span"
            className={classes.sectionTitle}
          >
            {title}
          </Typography>
          <div className={classes.headerLine} />
        </ButtonBase>
      </div>
      {isExpanded && (
        <div id={contentId} className={classes.sectionContent}>
          {nudges.map((nudge) => (
            <TechniqueCard
              key={nudge.id}
              techniqueName={nudge.technique}
              color={color}
              disabled={!canSchedule}
              onClick={() => onClick(nudge)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface ScheduleProps {
  client: ClientFragment
  canSchedule?: boolean
}

/**
 * Renders the scheduling workspace for a client.
 *
 * Displays searchable techniques grouped by domain and available assessments,
 * supports expand/collapse state per domain (persisted in session storage),
 * and opens the scheduling dialog when a technique or assessment is selected.
 *
 * @prop client: the ClientFragment whom we are scheduling for
 * @prop canSchedule: a boolean indicating whether scheduling is possible
 */
export default function Schedule({ client, canSchedule }: ScheduleProps) {
  const [searchInput, setSearchInput] = useState("")
  const searchQuery = useDeferredValue(searchInput)
  const [dialogItem, setDialogItem] = useState<ScheduleItem | UnscheduledItem>()
  const [domain, setDomain] = useState<NudgeDomain | null>(null)
  const [expandedSections, setExpandedSections] = useState<
    Partial<Record<NudgeDomain, boolean>>
  >({})

  const { user } = useUser()
  const { showBriteFeatures } = useOrganizationFeatureFlags()
  const graphqlClient = useGraphQLClient()

  const userId = user?.id
  const orgId = user?.organizations?.[0]?.id
  const EXPANDED_SECTIONS_STORAGE_KEY = userId
    ? `schedule-expanded-sections-${userId}`
    : null

  const { data: techniqueData, isLoading: isTechniqueDataLoading } =
    useGetActiveTechniquesAndInterventionsQuery(
      graphqlClient,
      {
        org_id: orgId,
        user_id: userId,
      },
      {
        enabled: !!userId && !!orgId,
      }
    )
  const nudges = techniqueData?.nudges ?? []
  const questionnaires = techniqueData?.questionnaires ?? []
  const { filteredNudgesByDomain, filteredQuestionnaires } = useTechniqueSearch(
    { searchQuery, nudges, questionnaires }
  )

  const isDomainVisible = (
    domainKey: NudgeDomain,
    selectedDomain: NudgeDomain | null,
    showBriteFeatures: boolean
  ) => {
    const isBriteOnlyDomain =
      domainKey === NudgeDomain.KEEP_ME_CALM ||
      domainKey === NudgeDomain.KEEP_ME_SAFE

    if (!showBriteFeatures && isBriteOnlyDomain) {
      return false
    }

    if (selectedDomain && domainKey !== selectedDomain) {
      return false
    }

    return true
  }
  const visibleDomains = nudgeOrder.filter((domainKey) =>
    isDomainVisible(domainKey, domain, showBriteFeatures)
  )
  const visibleTechniqueCount = visibleDomains.reduce(
    (count, domain) =>
      count + (filteredNudgesByDomain.get(domain)?.length ?? 0),
    0
  )
  const isSearching = searchInput.trim().length > 0
  const hasVisibleNudges = visibleTechniqueCount > 0

  // Need to wait for useUser to return these values to make techniques query
  const isWaitingForTechniqueQueryPrerequisites = !userId || !orgId
  const showLoading =
    !techniqueData &&
    (isTechniqueDataLoading || isWaitingForTechniqueQueryPrerequisites)

  const collapseOrExpandAll = (type: "collapse" | "expand") => {
    const shouldExpand = type === "expand"
    setExpandedSections(
      Object.fromEntries(nudgeOrder.map((domain) => [domain, shouldExpand]))
    )
  }

  const allSectionsExpanded = nudgeOrder.every(
    (nudge) => expandedSections[nudge] ?? true
  )

  // Fetches state of collapsed sections from sessionStorage and sets local state
  useEffect(() => {
    if (!EXPANDED_SECTIONS_STORAGE_KEY) return

    try {
      const stored = sessionStorage.getItem(EXPANDED_SECTIONS_STORAGE_KEY)
      setExpandedSections(stored ? JSON.parse(stored) : {})
    } catch {
      setExpandedSections({})
    }
  }, [EXPANDED_SECTIONS_STORAGE_KEY])

  // Writes state of collapsed section to localstorage
  useEffect(() => {
    if (!EXPANDED_SECTIONS_STORAGE_KEY) return

    sessionStorage.setItem(
      EXPANDED_SECTIONS_STORAGE_KEY,
      JSON.stringify(expandedSections)
    )
  }, [expandedSections, EXPANDED_SECTIONS_STORAGE_KEY])

  const classes = useStyles()
  const scheduleColors = colors.schedule
  return (
    <>
      <div className={classes.schedule}>
        <div className={classes.techniquesAndQuestionnaires}>
          <div className={classes.header}>
            <Typography variant="h3" className={classes.title}>
              Techniques
            </Typography>
            <div className={classes.headerSection}>
              <div className={classes.search}>
                <TextField
                  placeholder="Search Techniques"
                  variant="outlined"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  aria-label="search techniques"
                  InputProps={{
                    classes: {
                      root: classes.inputRoot,
                      input: classes.input,
                    },
                    startAdornment: <SearchIcon className={classes.icon} />,
                  }}
                />
              </div>
              <TechniqueFilter
                nudges={nudges}
                domain={domain}
                setDomain={setDomain}
              />
            </div>
          </div>

          {showLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress aria-label="loading techniques" />
            </div>
          ) : (
            <>
              {hasVisibleNudges && !isSearching && (
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.collapseExpandButton}
                  onClick={() => {
                    collapseOrExpandAll(
                      allSectionsExpanded ? "collapse" : "expand"
                    )
                  }}
                >
                  {allSectionsExpanded ? "Collapse" : "Expand"} All
                </Button>
              )}

              {!hasVisibleNudges && isSearching ? (
                <Typography variant="body1" className={classes.noTechniquesTxt}>
                  Your search did not return any techniques.
                </Typography>
              ) : (
                visibleDomains.map((nudge) => (
                  <TechniqueSection
                    key={nudge}
                    title={nudge}
                    nudges={filteredNudgesByDomain.get(nudge) ?? []}
                    color={scheduleColors[nudge]}
                    canSchedule={canSchedule}
                    onClick={(n) => setDialogItem(n)}
                    isExpanded={
                      isSearching ? true : expandedSections[nudge] ?? true
                    }
                    onToggleExpanded={() =>
                      !isSearching &&
                      setExpandedSections((prev) => ({
                        ...prev,
                        [nudge]: !(prev[nudge] ?? true),
                      }))
                    }
                  />
                ))
              )}

              {hasVisibleNudges && <div className={classes.borderLine} />}
              <Typography variant="h3" className={classes.secondaryTitle}>
                Assessments
              </Typography>
              <div className={classes.sectionContent}>
                {filteredQuestionnaires.length === 0 && isSearching ? (
                  <Typography variant="body1">
                    Your search did not return any assessments.
                  </Typography>
                ) : (
                  filteredQuestionnaires.map((questionnaire) => (
                    <TechniqueCard
                      key={questionnaire.name}
                      techniqueName={questionnaire.name}
                      color={scheduleColors.QUESTIONNAIRES}
                      disabled={!canSchedule}
                      onClick={() => setDialogItem(questionnaire)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
        <ScheduleOverview client={client} canSchedule={canSchedule ?? false} />
      </div>
      <ScheduleDialog
        open={!!dialogItem}
        client={client}
        onClose={() => setDialogItem(undefined)}
        item={dialogItem}
        canSchedule={canSchedule}
      />
    </>
  )
}

const useCardStyles = makeStyles((theme) => ({
  card: ({ border, background }: { border: string; background: string }) => ({
    backgroundColor: colors.neutral.white,
    padding: 0,
    border: 0,
    margin: 0,
    height: 44,
    width: "fit-content",
    display: "flex",
    alignItems: "center",
    borderWidth: 1,
    borderLeftWidth: 4,
    borderStyle: "solid",
    borderColor: border,
    borderRadius: 4,
    color: colors.neutral.denim,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    cursor: "pointer",
    "&:focus": {
      backgroundColor: background,
      borderColor: colors.neutral.denim,
      outline: "none",
    },
    "&:focus:active": {
      backgroundColor: background,
      borderColor: border,
      color: border,
      outline: "none",
    },
    "&:hover": {
      backgroundColor: background,
    },
  }),
  cardText: {
    fontWeight: 700,
    color: "inherit",
  },
  cardDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}))

const useStyles = makeStyles((theme) => ({
  schedule: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: theme.spacing(3),
  },
  techniquesAndQuestionnaires: {
    padding: theme.spacing(3),
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 4,
  },
  title: {
    fontWeight: 700,
    color: colors.neutral.denim,
  },
  headerLine: {
    height: 1,
    width: "100%",
    borderBottom: `1px solid ${colors.neutral.clay}`,
    marginLeft: theme.spacing(3),
  },
  section: {
    marginBottom: 36,
  },
  sectionContent: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  sectionHeaderButton: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: 0,
    margin: 0,
    textAlign: "left",
    zIndex: 0,
  },
  sectionHeaderIcon: {
    flexShrink: 0,
    color: colors.neutral.denim,
  },
  sectionTitle: {
    fontWeight: 700,
    color: colors.neutral.denim,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  collapseExpandButton: {
    color: colors.neutral.denim,
    borderColor: colors.neutral.clay,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    "&:hover": {
      backgroundColor: colors.primary.cloud,
      borderColor: colors.neutral.denim,
    },
  },
  borderLine: {
    height: 1,
    width: "100%",
    borderBottom: `1px solid ${colors.neutral.clay}`,
    marginBottom: theme.spacing(3),
  },
  secondaryTitle: {
    fontWeight: 700,
    color: colors.neutral.denim,
    marginBottom: 12,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },
  loadingContainer: {
    minHeight: 120,
    height: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  headerSection: {
    display: "flex",
    alignItems: "center",
  },
  search: {
    marginRight: theme.spacing(3),
    zIndex: 1000,
  },
  inputRoot: {
    backgroundColor: colors.primary.cloud,
    color: colors.neutral.asphalt,
    height: 44,
    width: 280,
    borderRadius: 12,
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${colors.primary.periwinkle}`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.primary.cloud,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.primary.midnight,
    },
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  input: {
    "&::placeholder": {
      color: colors.neutral.asphalt,
      opacity: 1,
    },
  },
  noTechniquesTxt: {
    padding: "1rem 0",
  },
}))
